import { useContext } from "react";
import { PropertyStoreContext } from "../../../contexts/PropertyStoreContext";
import { useCreatePromoTcState } from "./useCreatePromoTcState";
import { useCreatePromoFieldChange } from "./useCreatePromoFieldChange";
import { useCreatePromoValidation } from "./useCreatePromoValidation";
import { useCreatePromoSubmit } from "./useCreatePromoSubmit";

/**
 * Custom hook for managing CreatePromo component state.
 * Orchestrates targeting-conditions state, field-change handling,
 * validation, and the submit pipeline (pilot vs. standard).
 */
export function useCreatePromoState({
  promo,
  setPromo,
  ratePlans,
  rooms,
  // Property that `ratePlans`/`rooms` were fetched for. Combos built from
  // them are tagged with it so a selection can be matched back to its owner
  // (EO-1060 cross-property guard).
  ratePlansPropertyId = null,
  isEdit,
  isMarket,
  hideModal,
  setLoading,
  calculationType,
  isDuplicate = false,
  isPilotHotel = false,
  // A caller whose recommendation tracks its own state supplies how to record
  // it (Action Centre v2 does, keyed on the card id) — see
  // useCreatePromoSubmit.recordRecommendationUsed.
  onRecommendationUsed = null,
}) {
  const { selectedProperty, properties } = useContext(PropertyStoreContext);

  const { tc, setTc } = useCreatePromoTcState({ promo, isEdit, isDuplicate });

  const { handleFieldChange } = useCreatePromoFieldChange({
    promo,
    setPromo,
    rooms,
    ratePlans,
    ratePlansPropertyId,
    isEdit,
    isDuplicate,
    selectedProperty,
  });

  const { isLaunchDisabled, isDraftDisabled } = useCreatePromoValidation({
    promo,
    rooms,
    ratePlans,
    isMarket,
  });

  const { submitPromo } = useCreatePromoSubmit({
    promo,
    selectedProperty,
    properties,
    isEdit,
    isDuplicate,
    isMarket,
    isPilotHotel,
    rooms,
    ratePlans,
    ratePlansPropertyId,
    calculationType,
    hideModal,
    setLoading,
    onRecommendationUsed,
  });

  return {
    selectedProperty,
    isLaunchDisabled,
    isDraftDisabled,
    tc,
    setTc,
    handleFieldChange,
    submitPromo,
  };
}
